import React, { useMemo, useState } from 'react';
import { Menu, message, Popover, Tooltip } from 'antd';
import { useDispatch } from 'react-redux';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { useNavigate } from 'react-router-dom';
import LoaderSvg from '@components/Loader/LoaderSvg';
import { useAssetMVPermissions, useTypedSelector } from '@hooks';
import {
  setAsset,
  setToggleOpenTranscript,
  toggleFavoriteAssetInMV
} from '@redux/actions/mediaViewerAction';
import { useAuth } from '@hooks/useAuth';
import { useResponsive } from '@hooks/useResponsive';
import {
  toggleApprovalAssetModal,
  toggleApprovalModal,
  toggleCompareVersionsModal,
  toggleCopyAssetVersionModal,
  toggleEntityDeletionModal,
  toggleRenameEntityModal,
  toggleUploadAssetModal
} from '@redux/actions/modalAction';
import { download } from '@helpers/download';
import { nonNull } from '@helpers/non-null';
import { useCurrentWorkspace } from '@hooks/workspace';
import { ApprovalWorkflowItemDto } from '@api/Api';
import { useOrganization } from '@components/OrganizationBoundary';
import { LgWidth } from '@components/Responsive';
import ApprovalVersionLockedModal from '@components/MediaViewerHeader/components/ApprovalVersionLockedModal';
import {
  ApprovalRoutingIcon,
  CheckIcon,
  CopySecondaryIcon,
  CrossIcon,
  DeleteIcon,
  DownloadInvoiceIcon,
  MediaViewerCompareIcon,
  MediaViewerDownloadIcon,
  ActionIcon,
  EditIcon,
  SlideMicrophoneIcon,
  FavoriteIcon,
  SuccessIcon
} from '@icons';
import './AssetsActionMenu.less';

function AssetsActionMenu() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMdWidth, isSmWidth } = useResponsive();
  const { currentOrganization } = useOrganization(false);
  const [currentWorkspace] = useCurrentWorkspace(false);
  const canDownload = useAssetMVPermissions('download');
  const isInternal = user?.type === 'internal';
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const asset = useTypedSelector(({ mediaViewer }) => mediaViewer.assets);
  const isOpenTranscriptModal = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.isOpenTranscript
  );
  const [isShowMenu, setIsShowMenu] = useState<boolean>(false);
  const [openKeysSubMenu, setOpenKeysSubMenu] = useState<string[]>([]);
  const [isWarnModalVisible, setIsWarnModalVisible] = useState(false);
  const isLastVersion =
    selectedVersion?.versionNumber === asset?.asset?.versionsCount;
  const hasApprovalWorkflow = !!asset?.asset?.approvalWorkflow;
  const showApprovalButton =
    !!asset &&
    !!selectedVersion &&
    (hasApprovalWorkflow ||
      (isLastVersion &&
        !!currentOrganization?.entity?.features.approvalRouting));

  const canCreateTranscription =
    !!currentOrganization?.entity?.features.audioTranscription &&
    !!asset?.asset?.permissions.transcribe;

  const metadata = selectedVersion?.metadata;
  const isVideoOrAudio =
    metadata?.type === 'video' || metadata?.type === 'audio';
  const transcriptionStatus = isVideoOrAudio && metadata?.transcription?.status;

  const menuItems = useMemo(() => {
    const items: ItemType[] = [];
    const metadata = selectedVersion?.metadata;
    if (!isMdWidth) {
      const versions = asset?.asset?.versions ?? [];
      const isCompareVersionsDisabled =
        versions.length <= 1 && !asset?.asset?.permissions.uploadNewVersion;
      if (!isCompareVersionsDisabled) {
        items.push({
          key: 'compare-versions',
          icon: <MediaViewerCompareIcon />,
          label: 'Compare versions',
          onClick: () => {
            if (versions.length <= 1) {
              if (!asset?.workspaceId) return;
              dispatch(
                toggleUploadAssetModal({
                  visible: true,
                  newVersionFromMediaViewer: true,
                  workspaceId: asset.workspaceId
                })
              );
              return;
            }
            dispatch(
              toggleCompareVersionsModal({
                visible: true,
                assetVersions: versions
              })
            );
          }
        });
      }
    }
    if (metadata?.type === 'video' || metadata?.type === 'audio') {
      if (canCreateTranscription) {
        if (transcriptionStatus === 'ready') {
          const transcriptionFiles: ItemType[] = [];
          if (metadata.transcription.subRipUrl) {
            transcriptionFiles.push({
              key: 'transcription-srt-download',
              label: 'SRT',
              onClick: () => {
                setIsShowMenu(false);
                download(metadata.transcription.subRipUrl);
              }
            });
          }
          if (metadata.transcription.webVttUrl) {
            transcriptionFiles.push({
              key: 'transcription-vtt-download',
              label: 'VVT',
              onClick: () => {
                setOpenKeysSubMenu([]);
                setIsShowMenu(false);
                download(metadata.transcription.webVttUrl);
              }
            });
          }
          if (metadata.transcription.url) {
            transcriptionFiles.push({
              key: 'transcription-raw-download',
              label: 'RAW',
              onClick: () => {
                setOpenKeysSubMenu([]);
                setIsShowMenu(false);
                download(metadata.transcription.url);
              }
            });
          }
          if (transcriptionFiles.length > 0) {
            if (isMdWidth) {
              transcriptionFiles.push({
                key: 'cancel',
                label: 'Cancel'
              });
            }
            items.push({
              key: 'transcription-download',
              label: 'Download transcription',
              icon: <DownloadInvoiceIcon />,
              children: transcriptionFiles,
              popupOffset: [16, -16],
              popupClassName: 'media_viewer_submenu media_viewer_mobile-overlay'
            });
          }
        }
      }
    }
    if (showApprovalButton) {
      items.push({
        key: 'edit-approval-workflow',
        icon: <ApprovalRoutingIcon />,
        disabled: isSmWidth,
        className: 'assets-action-menu-item-approval-routing',
        label: asset.asset?.approvalWorkflow
          ? asset.asset?.approvalWorkflow.permissions.manage
            ? 'Edit approval routing'
            : 'View approval routing'
          : 'Route for approval'
      });
    }
    if (asset?.asset?.permissions.rename) {
      items.push({
        key: 'rename',
        icon: <EditIcon />,
        label: 'Rename media'
      });
    }

    if (asset?.asset?.permissions.uploadNewVersion) {
      items.push({
        key: 'copy-version',
        icon: <CopySecondaryIcon />,
        label: 'Copy as new version'
      });
    }
    items.push({
      key: 'add',
      icon: (
        <FavoriteIcon
          className={asset?.isFavorite ? 'favorite' : 'non_favorite'}
        />
      ),
      label: asset?.isFavorite ? 'Remove from favorites' : 'Add to favorites'
    });
    if (asset?.asset?.permissions.delete) {
      items.push({
        type: 'divider'
      });
      items.push({
        key: 'delete',
        icon: <DeleteIcon />,
        label: 'Delete'
      });
    }
    if (isMdWidth) {
      items.push({
        key: 'cancel',
        label: 'Cancel'
      });
    }
    return items;
  }, [
    showApprovalButton,
    asset?.asset?.versions,
    asset?.asset,
    asset?.isFavorite,
    selectedVersion?.metadata,
    canCreateTranscription,
    isMdWidth,
    isSmWidth,
    transcriptionStatus
  ]);

  const onUpdateAsset = (aw: ApprovalWorkflowItemDto | null) => {
    if (!asset) return;
    const updateAsset = {
      ...asset,
      asset: {
        ...nonNull(asset.asset),
        approvalWorkflow: aw
          ? {
              id: aw.id,
              status: aw.status,
              isPaused: aw.isPaused,
              isFinished: aw.isFinished,
              permissions: {
                manage: aw.permissions.manage,
                read: asset.asset?.approvalWorkflow?.permissions.read ?? true
              }
            }
          : null
      }
    };
    dispatch(setAsset(updateAsset));
  };

  const onHandlerMenu = ({ key }: { key: string }) => {
    setIsShowMenu(false);
    setOpenKeysSubMenu([]);
    if (!asset) return;
    const messageKey = 'assets-action-menu-success';
    switch (key) {
      case 'edit-approval-workflow':
        if (!selectedVersion) return;
        if (!isLastVersion) {
          setIsWarnModalVisible(true);
          return;
        }

        if (asset?.asset?.approvalWorkflow?.permissions?.manage) {
          dispatch(
            toggleApprovalModal({
              visible: true,
              approvalWorkflowId: asset.asset?.approvalWorkflow?.id,
              permissions: {
                create: !!currentWorkspace?.permissions?.createApprovalWorkflows
              },
              campaignId: asset.campaignId,
              callback: onUpdateAsset
            })
          );
          return;
        }
        if (asset?.asset?.approvalWorkflow) {
          dispatch(
            toggleApprovalAssetModal({
              assetId: asset.id,
              visible: true,
              versionId: selectedVersion.id,
              approvalWorkflowId: asset.asset?.approvalWorkflow.id,
              callback: onUpdateAsset
            })
          );
          return;
        }
        dispatch(
          toggleApprovalModal({
            visible: true,
            assetVersions: [
              {
                ...selectedVersion,
                assetId: asset.id
              }
            ],
            permissions: {
              create: !!currentWorkspace?.permissions?.createApprovalWorkflows
            },
            campaignId: asset.campaignId,
            callback: onUpdateAsset
          })
        );
        break;
      case 'add':
        if (asset.deletedAt) return;
        if (!asset?.isFavorite) {
          dispatch(
            toggleFavoriteAssetInMV({
              assetId: asset.id,
              isFavorite: !asset.isFavorite
            })
          );
          message.success({
            key: messageKey,
            className: 'message-dark-modal copy-success-toast',
            content: (
              <div className="message-dark-modal-content">
                <div>Media has been added to favorites</div>
                <CrossIcon
                  onClick={() => message.destroy(messageKey)}
                  className="message-dark-modal-close-btn"
                />
              </div>
            ),
            icon: <SuccessIcon className="anticon" />
          });
          return;
        }
        dispatch(
          toggleFavoriteAssetInMV({
            assetId: asset.id,
            isFavorite: !asset.isFavorite
          })
        );
        message.success({
          key: messageKey,
          className: 'message-dark-modal copy-success-toast',
          content: (
            <div className="message-dark-modal-content">
              <div>Media has been removed from favorites</div>
              <CrossIcon
                onClick={() => message.destroy(messageKey)}
                className="message-dark-modal-close-btn"
              />
            </div>
          ),
          icon: <SuccessIcon className="anticon" />
        });
        break;
      case 'rename':
        dispatch(
          toggleRenameEntityModal({
            entity: asset,
            page: 'media',
            visible: true,
            versionId: selectedVersion?.id
          })
        );
        break;
      case 'delete':
        dispatch(
          toggleEntityDeletionModal({
            entity: asset,
            page: 'asset',
            permanent: false,
            visible: true,
            func: () => {
              if (asset?.campaignId) {
                navigate(`/campaigns/single/${asset.campaignId}`);
                return;
              }
              if (asset?.workspaceId) {
                navigate(`/campaigns/all/${asset?.workspaceId}`);
                return;
              }
              navigate(`/`);
            }
          })
        );
        break;
      case 'copy-version': {
        if (!selectedVersion) return;
        const lastVersion = nonNull(asset.asset).versions[0];
        const aw = asset.asset?.approvalWorkflow;
        const isAssetWithAW = !!aw && !aw.isPaused;
        dispatch(
          toggleCopyAssetVersionModal({
            visible: true,
            assetId: asset.id,
            versionId: selectedVersion.id,
            assetName: `${lastVersion.name}.${lastVersion.extension}`,
            isAssetWithAW
          })
        );
        break;
      }
      default:
      // Do nothing
    }
  };

  const videoDownloadMenu: ItemType[] = [];
  if (canDownload && selectedVersion?.metadata.type === 'video') {
    if (selectedVersion.sourceUrl) {
      const videoHeight = selectedVersion.metadata.info.avInfo?.video?.height;
      videoDownloadMenu.push({
        key: 'source',
        onClick: () => download(selectedVersion.sourceUrl),
        label: videoHeight ? `Original ${videoHeight}p` : 'Original'
      });
    }
    videoDownloadMenu.push(
      ...Object.entries(selectedVersion.metadata.videoProxies)
        .reverse()
        .filter((el) => el[1].status !== 'skipped')
        .map((el) => {
          return {
            key: el[0],
            disabled: el[1].status !== 'ready',
            onClick: () => download(el[1].url),
            label: `${el[0]}p`
          };
        })
    );
  }

  return (
    <div className="assets-action-menu">
      {canCreateTranscription && isVideoOrAudio && (
        <LgWidth>
          <Tooltip
            title={
              transcriptionStatus === 'pending'
                ? 'Transcript in progress'
                : 'Transcript'
            }
            overlayClassName="toggle_favorite_overlay"
            placement="bottom"
          >
            <div
              className="icon"
              onClick={() => {
                if (transcriptionStatus === 'pending') return;
                dispatch(setToggleOpenTranscript(!isOpenTranscriptModal));
              }}
            >
              {transcriptionStatus === 'ready' && (
                <span className="actions-transcript-status-icon">
                  <CheckIcon />
                </span>
              )}
              {transcriptionStatus === 'pending' && (
                <span className="actions-transcript-status-icon">
                  <LoaderSvg
                    spinSize={10}
                    strokeWidth={10}
                    circleColor="rgba(255, 255, 255, 0.3)"
                    circleStrokeColor="#fff"
                  />
                </span>
              )}
              <SlideMicrophoneIcon />
            </div>
          </Tooltip>
        </LgWidth>
      )}
      <LgWidth>
        {canDownload &&
          !!selectedVersion?.sourceUrl &&
          (selectedVersion.metadata.type === 'video' ? (
            <Popover
              trigger={['click']}
              placement="leftTop"
              overlayClassName="media_viewer_information_overlay"
              content={
                <Menu
                  selectable={false}
                  prefixCls="media_viewer_menu-menu"
                  items={videoDownloadMenu}
                />
              }
            >
              <Tooltip
                title="Download"
                overlayClassName="toggle_favorite_overlay"
                placement="bottom"
              >
                <div className="icon">
                  <MediaViewerDownloadIcon />
                </div>
              </Tooltip>
            </Popover>
          ) : (
            <div
              onClick={() => download(selectedVersion.sourceUrl)}
              className="icon"
            >
              <Tooltip
                title="Download"
                overlayClassName="toggle_favorite_overlay"
                placement="bottom"
              >
                <MediaViewerDownloadIcon />
              </Tooltip>
            </div>
          ))}
      </LgWidth>

      {isInternal && (
        <Popover
          placement="leftTop"
          overlayClassName="media_viewer_information_overlay media_viewer_mobile-overlay"
          open={
            (isShowMenu && !isMdWidth) ||
            (isShowMenu && isMdWidth && !openKeysSubMenu.length)
          }
          onOpenChange={(value) => {
            setIsShowMenu(value);
          }}
          content={
            <Menu
              prefixCls="media_viewer_menu-menu"
              selectable={false}
              onClick={onHandlerMenu}
              onOpenChange={(e) => {
                setOpenKeysSubMenu(e);
              }}
              items={menuItems}
            />
          }
        >
          <div className="icon">
            <ActionIcon />
          </div>
        </Popover>
      )}
      {asset && (
        <ApprovalVersionLockedModal
          visible={isWarnModalVisible}
          assetId={asset.id}
          latestVersionId={asset.asset?.versions[0]?.id}
          onCancel={() => setIsWarnModalVisible(false)}
        />
      )}
    </div>
  );
}

export default AssetsActionMenu;
